import React, { useMemo, useState } from 'react';
import Button from '../ui/Button';
import DoorScreen from '../event-day/DoorScreen';
import { createDoorApi } from '../../services/DoorApiClient';
import { clearKioskSession, kioskCredentials, kioskLogin, readKioskSession } from '../../services/kioskSession';
import { errorMessageOf } from '../../utils/apiErrors';
import '../../styles/components/DoorScreen.css';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The event a kiosk link names: /kiosk?event=<uuid>. Anything else is no event. */
export function eventFromLink(search: string): string | null {
  const id = new URLSearchParams(search).get('event');
  return id && UUID.test(id) ? id.toLowerCase() : null;
}

/**
 * The page a door tablet or phone opens from the link the event owner shares (/kiosk?event=<id>). It asks for the event's kiosk
 * password once, keeps the event-bound session for the tab, and shows the door for that one event. It sits outside the normal
 * sign-in on purpose: a kiosk is not a planner and can reach nothing else.
 */
const KioskPage: React.FC = () => {
  const eventId = eventFromLink(window.location.search);
  const [signedIn, setSignedIn] = useState<boolean>(() => (eventId ? readKioskSession(eventId) !== null : false));
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const api = useMemo(() => createDoorApi(kioskCredentials), []);

  if (!eventId) {
    return (
      <main className="kiosk">
        <div className="kiosk-login" role="alert">
          <h1>This link is not complete</h1>
          <p>Ask the event owner for the kiosk link for your event. It looks like the page you were sent, with an event in it.</p>
        </div>
      </main>
    );
  }

  const signOut = () => { clearKioskSession(); setSignedIn(false); setPassword(''); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true); setError(null);
    try {
      await kioskLogin(eventId, password);
      setPassword('');
      setSignedIn(true);
    } catch (err) {
      setError(errorMessageOf(err, 'Could not sign in. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (!signedIn) {
    return (
      <main className="kiosk">
        <form className="kiosk-login" onSubmit={submit} aria-label="Kiosk sign-in">
          <h1>Event day sign-in</h1>
          <label className="door-field">
            <span>Kiosk password</span>
            <input className="door-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="off" autoCapitalize="characters" autoFocus required />
          </label>
          {error && <p className="door-walkin__error" role="alert">{error}</p>}
          <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!password}>Open the guest list</Button>
        </form>
      </main>
    );
  }

  return (
    <main className="kiosk">
      <header className="kiosk__bar">
        <h1 className="kiosk__title">Event day</h1>
        <Button variant="secondary" size="lg" onClick={signOut}>Sign out</Button>
      </header>
      <DoorScreen api={api} eventId={eventId} onSignInAgain={signOut} credentials={kioskCredentials} />
    </main>
  );
};

export default KioskPage;
